import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useMe } from '../lib/queries.js';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AIConsentModal({ open, onClose, onSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const qc = useQueryClient();

  if (!open) return null;

  async function handleAccept() {
    setLoading(true);
    try {
      await api('/privacy/consent', {
        method: 'POST',
        body: { scope: 'ai_processing', granted: true, ip: '0.0.0.0', userAgent: navigator.userAgent },
      });
      await qc.invalidateQueries({ queryKey: ['me'] });
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to grant AI consent', err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="p-6">
          <div className="w-12 h-12 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-2xl">neurology</span>
          </div>
          <h2 className="text-xl font-semibold mb-2">Recursos de Inteligência Artificial</h2>
          <p className="text-sm text-zinc-400 mb-6">
            Para utilizar a IA do Flowmindy, precisamos do seu consentimento. Seus dados (como textos de nós e conexões selecionadas) serão enviados temporariamente para nossos parceiros de IA (ex: OpenAI, Google) apenas para processamento da sua solicitação. Nós não usamos seus dados para treinar modelos próprios.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleAccept}
              disabled={loading}
              className="px-4 py-2 text-sm bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <span className="material-symbols-outlined animate-spin text-sm">progress_activity</span>
              ) : null}
              Eu concordo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
